import SignaturePad from "signature_pad";
import { confirm as confirmPopup } from "../framework/components/popup.js";

/*
Hook driving the on-screen attendance signature pad of the instructor cockpit: the instructor hands
their tablet or phone over and the signatory draws with a finger or a stylus.

Expected markup, rendered by AttendanceSlotPanel inside the <dialog> popup:

  <div id="attendance-signature-pad-trainee-42" phx-hook="AttendanceSignaturePad" phx-update="ignore"
       data-signatory-kind="trainee" data-signatory-id="42">
    <canvas class="cockpit-attendance-slot-panel__signature-canvas"></canvas>
    <p class="cockpit-attendance-slot-panel__signature-hint">…</p>
    <p role="alert" data-signature-error data-empty-message="Signez avant d'enregistrer…"></p>
    <button type="button" data-signature-clear>…</button>
  </div>

- `phx-update="ignore"` keeps LiveView away from the canvas: a patch replacing the node would drop
  the strokes being drawn,
- the id carries the signatory, so picking someone else destroys and re-mounts this hook, i.e. hands
  out a clean pad.

Save is triggered by the popup footer through `JS.dispatch("attendance-signature:save")`, a purely
client-side event: the pad, not the server, decides whether there is something to save.

Pushed to the LiveComponent: "save_tablet_signature" %{kind, id, signature}, the signature being a
"data:image/png;base64,…" URL. The component always replies %{ok: boolean}: the pad locks itself
while the save is in flight, and only that reply can unlock it — LiveView never removes an attribute
the hook set on an ignored subtree, so the server cannot undo the lock by re-rendering.
*/

// A phone can report a ratio of 3 or 4: past 2 the exported PNG grows for no visible gain on a
// signature. Combined with the CSS max-width, this caps what travels over the websocket.
const MAX_PIXEL_RATIO = 2;

// Hard client-side ceiling (bytes of the data URL), well below both the server-side validation and
// the websocket frame limit. A capped canvas never gets close; this only guards against a browser
// producing an unexpectedly heavy export.
const MAX_DATA_URL_BYTES = 1_500_000;
const FALLBACK_EXPORT_WIDTH = 800;

const AttendanceSignaturePadHook = {
  mounted() {
    this.canvas = this.el.querySelector("canvas");
    this.errorEl = this.el.querySelector("[data-signature-error]");
    this.saving = false;
    this.confirming = false;

    this.pad = new SignaturePad(this.canvas, {
      // The PNG is stored as-is and reprinted on attendance sheets, which expect an opaque image.
      // signature_pad defaults to a transparent background since v4.
      backgroundColor: "rgb(255, 255, 255)",
      penColor: "rgb(0, 0, 0)",
      minWidth: 0.7,
      maxWidth: 2.5,
      throttle: 16,
      minDistance: 3,
    });

    // Fires once when the observation starts, which performs the initial sizing as soon as the
    // <dialog> has a layout, then on every box change (rotation, split view, keyboard).
    this.observer = new ResizeObserver(() => this.resize());
    this.observer.observe(this.canvas);

    this.onSave = () => this.save();
    this.onClear = () => this.clear();
    this.onBeginStroke = () => this.clearError();

    this.clearButton = this.el.querySelector("[data-signature-clear]");

    this.el.addEventListener("attendance-signature:save", this.onSave);
    this.clearButton?.addEventListener("click", this.onClear);
    this.pad.addEventListener("beginStroke", this.onBeginStroke);
  },

  destroyed() {
    this.observer?.disconnect();
    this.el.removeEventListener("attendance-signature:save", this.onSave);
    this.clearButton?.removeEventListener("click", this.onClear);
    this.pad?.removeEventListener("beginStroke", this.onBeginStroke);
    // Unbinds signature_pad's own pointer listeners on the (detached) canvas.
    this.pad?.off();
    this.pad = null;
  },

  clear() {
    this.pad.clear();
    this.clearError();
  },

  showEmptyError() {
    if (this.errorEl) this.errorEl.textContent = this.errorEl.dataset.emptyMessage;
  },

  clearError() {
    if (this.errorEl) this.errorEl.textContent = "";
  },

  // Undoes what `save()` locked, so the signatory can draw again and retry. Only called for a save
  // the server refused: a recorded one takes the pad away altogether.
  unlock() {
    this.saving = false;
    this.el.removeAttribute("aria-busy");
  },

  /*
  Keeps the backing store in sync with the CSS box: a canvas is sized in device pixels, so the box
  is multiplied by the (capped) device pixel ratio and the context scaled back, otherwise the
  strokes are blurry on a retina screen.

  Resizing wipes the bitmap, so the strokes are saved and replayed rather than cleared. And we bail
  out when the pixel size did not actually change: mobile browsers fire resizes for unrelated
  reasons (address bar collapsing, keyboard) which would otherwise destroy a signature in progress.
  */
  resize() {
    const ratio = Math.min(Math.max(window.devicePixelRatio || 1, 1), MAX_PIXEL_RATIO);
    const width = Math.round(this.canvas.offsetWidth * ratio);
    const height = Math.round(this.canvas.offsetHeight * ratio);

    if (width === 0 || height === 0) return;
    if (width === this.canvas.width && height === this.canvas.height) return;

    const strokes = this.pad.toData();

    this.canvas.width = width;
    this.canvas.height = height;
    // Setting width/height resets the context transform, so the scale is applied again here.
    this.canvas.getContext("2d").scale(ratio, ratio);

    // fromData repaints the background then re-renders the strokes. Their coordinates are CSS
    // pixels, so they survive a pure pixel-ratio change untouched; a rotation may crop them, which
    // is still much better than wiping the signature.
    this.pad.fromData(strokes);
  },

  async save() {
    if (this.saving || this.confirming) return;

    if (this.pad.isEmpty()) {
      // Announced by the error region, cleared on the next stroke. Nothing is pushed and the popup
      // stays open.
      this.showEmptyError();
      return;
    }

    // Exported before the confirmation so the image is exactly the one the recap was shown for.
    const signature = this.exportDataUrl();

    if (!(await this.confirmed())) return;

    this.saving = true;
    this.el.setAttribute("aria-busy", "true");

    const payload = {
      kind: this.el.dataset.signatoryKind,
      id: this.el.dataset.signatoryId,
      signature,
    };

    this.pushEventTo(this.el, "save_tablet_signature", payload, (reply) => {
      if (!reply?.ok) this.unlock();
    });
  },

  /*
  Recap of who signs, for which slot, before the signature is recorded: it flips the trainee to
  "signed", which the cockpit cannot undo. Cancelling resolves to false and leaves the pad as it is,
  drawing included — nothing reaches the server.

  The `confirming` guard matters: showModal() throws on an already-open dialog, so a second save
  while the recap is up must not re-open it.
  */
  async confirmed() {
    const dialog = document.getElementById(this.el.dataset.confirmPopup);
    if (!dialog) return true;

    this.confirming = true;

    try {
      return await confirmPopup(dialog);
    } finally {
      this.confirming = false;
    }
  },

  // Full-resolution PNG, kept small by the CSS max-width and MAX_PIXEL_RATIO (a few tens of kB).
  // Should it ever exceed the ceiling, re-export from an offscreen canvas at a fixed width rather
  // than risk a websocket frame the server would refuse.
  exportDataUrl() {
    const dataUrl = this.pad.toDataURL();
    if (dataUrl.length <= MAX_DATA_URL_BYTES) return dataUrl;

    const scaled = document.createElement("canvas");
    scaled.width = FALLBACK_EXPORT_WIDTH;
    scaled.height = Math.round((this.canvas.height / this.canvas.width) * FALLBACK_EXPORT_WIDTH);
    scaled.getContext("2d").drawImage(this.canvas, 0, 0, scaled.width, scaled.height);

    return scaled.toDataURL("image/png");
  },
};

export default AttendanceSignaturePadHook;
